import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { LayoutGrid, RefreshCw, Table2, Target } from 'lucide-react';
import EmptyState from '../../components/ui/EmptyState';
import { outreachApi } from '../api/outreachApi';
import LeadDetailPanel from '../components/LeadDetailPanel';
import LeadStatusPicker from '../components/LeadStatusPicker';
import '../outreach.css';

const PAGE_SIZE = 50;
const EMPTY_FILTERS = {
  search: '',
  pipeline_status: '',
  engagement_status: '',
  campaign_id: '',
  follow_up: '',
  unlabelled_replies: false,
};

const isOverdue = (lead) => lead.follow_up_at && new Date(lead.follow_up_at) < new Date().setHours(0, 0, 0, 0);

export default function LeadsPage() {
  const { t } = useTranslation('common');

  // The chosen view lives in the URL, so a reload or a shared link opens the
  // same board rather than resetting to the table.
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'board' ? 'board' : 'table';
  const setView = (next) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (next === 'board') updated.set('view', 'board');
        else updated.delete('view');
        return updated;
      },
      { replace: true }
    );
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [board, setBoard] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [today, setToday] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState([]);
  const [colleagueNotice, setColleagueNotice] = useState(null);
  const [applyToAll, setApplyToAll] = useState(false);
  const [openLeadId, setOpenLeadId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const queryParams = useMemo(() => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value !== false) params[key] = value;
    });
    return params;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusList, todayCounts, campaignList] = await Promise.all([
        outreachApi.listPipelineStatuses(),
        outreachApi.leadsToday(),
        outreachApi.listCampaigns(),
      ]);
      setStatuses(statusList);
      setToday(todayCounts);
      setCampaigns(campaignList);

      if (view === 'board') {
        const { pipeline_status: ignored, follow_up: alsoIgnored, ...boardParams } = queryParams;
        setBoard(await outreachApi.leadBoard(boardParams));
      } else {
        const data = await outreachApi.listLeads({ ...queryParams, limit: PAGE_SIZE, offset: page * PAGE_SIZE });
        setLeads(data.leads);
        setTotal(data.total);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [view, queryParams, page]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (patch) => {
    setPage(0);
    setSelected([]);
    setFilters({ ...filters, ...patch });
  };

  const reportSideEffects = (result) => {
    setColleagueNotice(result?.colleagues_stopped ? result.colleagues_stopped : null);
  };

  const applyToLead = (leadId) => async (payload) => {
    reportSideEffects(await outreachApi.setLeadStatus(leadId, payload));
    await load();
  };

  const applyToSelection = async (payload) => {
    reportSideEffects(await outreachApi.bulkSetLeadStatus({ ...payload, leadIds: selected }));
    setSelected([]);
    await load();
  };

  const applyToEverythingMatching = async (payload) => {
    reportSideEffects(await outreachApi.bulkSetLeadStatus({ ...payload, filters: queryParams }));
    setSelected([]);
    setApplyToAll(false);
    await load();
  };

  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);

  const statusName = (key) => statuses.find((s) => s.key === key)?.name || key;
  const pages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="unibox">
      <div className="toolbar">
        <h1 className="toolbar-title">{t('outreach.leads.title')}</h1>
        <div className="toolbar-filters" />
        <div className="toolbar-actions">
          <button className="unibox-refresh" onClick={() => setView(view === 'table' ? 'board' : 'table')}>
            {view === 'table' ? <LayoutGrid size={14} /> : <Table2 size={14} />}
            {view === 'table' ? t('outreach.leads.boardView') : t('outreach.leads.tableView')}
          </button>
          <button className="unibox-refresh" onClick={load}>
            <RefreshCw size={14} /> {t('outreach.refresh')}
          </button>
        </div>
      </div>

      {today && (
        <div className="lead-today">
          <button
            className={`lead-today-card${today.overdue_follow_ups ? ' lead-today-card-alert' : ''}`}
            onClick={() => setFilter({ follow_up: 'overdue', unlabelled_replies: false })}
          >
            <span className="lead-today-number">{today.overdue_follow_ups}</span>
            {t('outreach.leads.today.overdue')}
          </button>
          <button className="lead-today-card" onClick={() => setFilter({ follow_up: 'today', unlabelled_replies: false })}>
            <span className="lead-today-number">{today.due_today}</span>
            {t('outreach.leads.today.dueToday')}
          </button>
          <button className="lead-today-card" onClick={() => setFilter({ unlabelled_replies: true, follow_up: '' })}>
            <span className="lead-today-number">{today.unlabelled_replies}</span>
            {t('outreach.leads.today.unlabelled')}
          </button>
          <button
            className="lead-today-card"
            onClick={() => setFilter({ engagement_status: 'replied', unlabelled_replies: false, follow_up: '' })}
          >
            <span className="lead-today-number">{today.new_replies_24h}</span>
            {t('outreach.leads.today.replies24h')}
          </button>
        </div>
      )}

      <div className="lead-filters">
        <input
          className="unibox-select"
          placeholder={t('outreach.leads.searchPlaceholder')}
          value={filters.search}
          onChange={(e) => setFilter({ search: e.target.value })}
        />
        <select
          className="unibox-select"
          value={filters.campaign_id}
          onChange={(e) => setFilter({ campaign_id: e.target.value })}
        >
          <option value="">{t('outreach.leads.allCampaigns')}</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          className="unibox-select"
          value={filters.engagement_status}
          onChange={(e) => setFilter({ engagement_status: e.target.value })}
        >
          <option value="">{t('outreach.leads.allEngagement')}</option>
          {['not_started', 'in_progress', 'completed', 'replied', 'bounced_hard', 'unsubscribed', 'ooo_paused', 'stopped', 'failed'].map(
            (key) => (
              <option key={key} value={key}>
                {t(`outreach.leads.engagement.${key}`, key)}
              </option>
            )
          )}
        </select>
        {(filters.search || filters.campaign_id || filters.engagement_status || filters.follow_up || filters.unlabelled_replies) && (
          <button className="unibox-page-button" onClick={() => setFilter(EMPTY_FILTERS)}>
            {t('outreach.leads.clearFilters')}
          </button>
        )}
      </div>

      {selected.length > 0 && (
        <div className="lead-bulk-bar">
          <span>
            {applyToAll
              ? t('outreach.leads.allMatchingCount', { count: total })
              : t('outreach.leads.selectedCount', { count: selected.length })}
          </span>
          <LeadStatusPicker
            statuses={statuses}
            value=""
            onApply={applyToAll ? applyToEverythingMatching : applyToSelection}
          />
          {total > selected.length && (
            <label className="campaign-checkbox">
              <input type="checkbox" checked={applyToAll} onChange={() => setApplyToAll(!applyToAll)} />
              {t('outreach.leads.applyToAllMatching', { count: total })}
            </label>
          )}
          <button className="unibox-page-button" onClick={() => { setSelected([]); setApplyToAll(false); }}>
            {t('outreach.leads.clearSelection')}
          </button>
        </div>
      )}

      {error && <div className="unibox-banner">{String(error)}</div>}
      {colleagueNotice && (
        <div className="unibox-banner">
          {t('outreach.leads.colleaguesStopped', { count: colleagueNotice })}{' '}
          <button className="unibox-page-button" onClick={() => setColleagueNotice(null)}>
            {t('outreach.leads.dismiss')}
          </button>
        </div>
      )}

      <div className="lead-layout">
        <div className="lead-main">
          {loading && <div className="unibox-empty">{t('outreach.loading')}</div>}

          {!loading && view === 'table' && total === 0 && (
            <EmptyState
              icon={<Target size={28} />}
              title={t('outreach.leads.empty')}
              text={t('outreach.leads.emptyHint')}
            >
              <Link className="unibox-button" to="/outreach/contacts">
                {t('outreach.importCsv')}
              </Link>
            </EmptyState>
          )}

          {!loading && view === 'table' && total > 0 && (
            <>
              <div className="lead-table-scroll">
              <table className="admin-table lead-table">
                <thead>
                  <tr>
                    <th />
                    <th>{t('outreach.leads.email')}</th>
                    <th>{t('outreach.leads.company')}</th>
                    <th>{t('outreach.leads.engagementStatus')}</th>
                    <th>{t('outreach.leads.pipelineStatus')}</th>
                    <th>{t('outreach.leads.followUpAt')}</th>
                    <th>{t('outreach.leads.lastActivity')}</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id} className={isOverdue(lead) ? 'lead-row-overdue' : ''}>
                      <td>
                        <input type="checkbox" checked={selected.includes(lead.id)} onChange={() => toggle(lead.id)} />
                      </td>
                      <td>
                        <button className="lead-email-button" onClick={() => setOpenLeadId(lead.id)}>
                          {lead.email}
                        </button>
                        {lead.suggested_status && (
                          <span className="lead-suggestion">
                            {t('outreach.leads.suggested', { status: statusName(lead.suggested_status) })}
                          </span>
                        )}
                      </td>
                      <td>{lead.company || '-'}</td>
                      <td>
                        <span className="unibox-badge">
                          {t(`outreach.leads.engagement.${lead.engagement_status}`, lead.engagement_status)}
                        </span>
                      </td>
                      <td>
                        <LeadStatusPicker
                          compact
                          statuses={statuses}
                          value={lead.pipeline_status}
                          onApply={applyToLead(lead.id)}
                        />
                      </td>
                      <td>{lead.follow_up_at ? new Date(lead.follow_up_at).toLocaleDateString() : '-'}</td>
                      <td>{lead.last_activity_at ? new Date(lead.last_activity_at).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              <div className="unibox-pagination">
                <button className="unibox-page-button" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  {t('outreach.previous')}
                </button>
                <span>{t('outreach.leads.pageOf', { page: page + 1, pages })}</span>
                <button className="unibox-page-button" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>
                  {t('outreach.next')}
                </button>
                <span className="unibox-spacer" />
                <span>{t('outreach.leads.totalCount', { count: total })}</span>
              </div>
            </>
          )}

          {!loading && view === 'board' && board && (
            <div className="lead-board">
              {board.columns.map((column) => (
                <div
                  key={column.status.key}
                  className="lead-board-column"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={async (e) => {
                    const leadId = e.dataTransfer.getData('text/plain');
                    if (!leadId) return;
                    if (column.status.requires_date) {
                      window.alert(t('outreach.leads.dropNeedsDate'));
                      return;
                    }
                    await applyToLead(leadId)({ pipelineStatus: column.status.key, followUpAt: null });
                  }}
                >
                  <div className="lead-board-head" style={{ borderTopColor: column.status.color }}>
                    <strong>{column.status.name}</strong>
                    <span className="unibox-badge">{column.total}</span>
                  </div>
                  {column.leads.map((lead) => (
                    <div
                      key={lead.id}
                      className={`lead-card${isOverdue(lead) ? ' lead-card-overdue' : ''}`}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData('text/plain', lead.id)}
                      onClick={() => setOpenLeadId(lead.id)}
                    >
                      <div className="lead-card-email">{lead.email}</div>
                      {lead.company && <div className="lead-card-company">{lead.company}</div>}
                      <span className="unibox-badge">
                        {t(`outreach.leads.engagement.${lead.engagement_status}`, lead.engagement_status)}
                      </span>
                    </div>
                  ))}
                  {column.total > column.leads.length && (
                    <button className="unibox-page-button" onClick={() => setFilter({ pipeline_status: column.status.key })}>
                      {t('outreach.leads.showAllInColumn', { count: column.total })}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {openLeadId && (
          <LeadDetailPanel
            // Forces a fresh instance per lead - without it, an armed
            // erase-confirm (or an in-progress notes edit) for the
            // previous lead stays armed after switching to a new one.
            key={openLeadId}
            leadId={openLeadId}
            statuses={statuses}
            onClose={() => setOpenLeadId(null)}
            onChanged={load}
          />
        )}
      </div>
    </div>
  );
}
